import type { ActivationOptionsResponse, InvitationActivateRequest, InvitationApiError, InvitationValidationReason } from '@bingads-webui-campaign-react/accept-invitation-api';
import { buildBackToInvitationUrl, buildLogoutUrl, buildReauthUrl } from '../query-params';
import { hasAttemptedReauth } from './activation-persistence';

// Where to navigate on a 401 from a session-bound API. `ReauthRequired` means the browser session is
// still valid but the SERVER's token cache lost this user's entry, so bouncing the browser through
// the challenge endpoint re-mints the tokens against the still-live IdP session and returns here with
// the invitation code intact — something the failed XHR could not do itself.
//
// Returns '' to mean "render the error fallback" (which keeps the code). That is deliberately the
// answer for a SECOND consecutive `ReauthRequired`: the challenge did not repair the cache, and
// bouncing again would loop forever because a JS navigation never trips the browser's redirect limit.
//
// Anything else — a plain `NotAuthenticated` 401, or an off-origin (rejected) authUrl — signs the
// user out, but back to the invitation rather than to a bare sign-out that drops the code and leaves
// them with no way to return to it. `buildLogoutUrl` remains the last resort for an unconfigured
// sign-out URL, where both builders yield '' and the caller renders the error fallback.
export const resolveSessionRecoveryUrl = (reauthUrlFromServer: string | undefined, code: string): string => {
  if (reauthUrlFromServer) {
    if (hasAttemptedReauth(code)) {
      return '';
    }
    const reauthUrl = buildReauthUrl(reauthUrlFromServer);
    if (reauthUrl) {
      return reauthUrl;
    }
  }
  return buildBackToInvitationUrl(code) || buildLogoutUrl();
};

const knownInvitationReasons: InvitationValidationReason[] = ['Expired', 'Invalid', 'UsersLimitReached', 'Inactive'];

// Maps the `acceptinvitationdebug` reason aliases to a reason so the friendly page can be previewed
// locally without reproducing a failing Validate/Activate (mirrors the pre-auth landing param).
export const debugInvitationReasonMap: Record<string, InvitationValidationReason> = {
  expired: 'Expired',
  invalid: 'Invalid',
  userslimitreached: 'UsersLimitReached',
  inactive: 'Inactive',
};

// Maps an error from Validate (structured `reason`) or Activate (CCMT code / raw fault message) to a
// terminal invitation-code reason so the post-auth page can render the friendly reason page instead
// of the raw CCMT fault or the generic fallback. Returns null for transient/retryable errors, which
// keep their existing (form / fallback) rendering.
export const getTerminalInvitationReason = (error?: InvitationApiError): InvitationValidationReason | null => {
  if (!error) {
    return null;
  }
  if (error.reason && knownInvitationReasons.includes(error.reason as InvitationValidationReason)) {
    return error.reason as InvitationValidationReason;
  }
  // The specific CCMT code may ride in errorCodes, the wrapper, or (for a raw sender fault) the message.
  const haystack = [...(error.errorCodes || []), error.error || '', error.message || ''].join(' ');
  if (/UserInvitationCode(Invalid|NotFound)|InvitationCodeInvalid/i.test(haystack)) {
    return 'Invalid';
  }
  if (/UserInvitationCodeExpired|InvitationExpired/i.test(haystack)) {
    return 'Expired';
  }
  if (/UsersLimitReached/i.test(haystack)) {
    return 'UsersLimitReached';
  }
  return null;
};

// PII-tolerant one-line summary of an invitation API error for the retry toast.
export const formatErrorDetail = (error: InvitationApiError): string => {
  const parts: string[] = [];
  if (error.status !== undefined) parts.push(`HTTP ${error.status}`);
  if (error.error) parts.push(error.error);
  if (error.errorCodes && error.errorCodes.length > 0) parts.push(error.errorCodes.join(', '));
  if (error.message) parts.push(error.message);
  return parts.join(' — ') || 'Unknown error';
};

// The activating user's LCID for the Activate userModel — server-provided user locale, else the
// culture LCID, else en-US (1033).
export const getLanguageId = (): number => {
  const win = window as any;
  const localeId = Number(win?.serverContext?.CurrentUser?.LocaleID);
  if (Number.isFinite(localeId) && localeId > 0) {
    return localeId;
  }

  const cultureLcid = Number(win?.serverContext?.CultureLcid);
  if (Number.isFinite(cultureLcid) && cultureLcid > 0) {
    return cultureLcid;
  }

  return 1033;
};

// The Activate payload for the consent-only path, where the existing user already has a complete
// address so no form is rendered. We echo their master data back, mirroring exactly what the legacy
// pre-filled profile form posts on Save: sending an empty/addressless userModel makes the MT resolve
// the user by PUID and return 200 WITHOUT consuming the invitation, so the address must be carried
// forward.
export const buildConsentOnlyActivateRequest = (
  routeContext: { code: string; intent?: string; idP?: string },
  options?: ActivationOptionsResponse
): InvitationActivateRequest => {
  const existingUser = options?.existingUser;
  const existingAddress = existingUser?.address;

  return {
    activationCode: routeContext.code,
    intent: routeContext.intent,
    identityProvider: routeContext.idP || options?.identityType || 'MSA',
    userModel: {
      firstName: existingUser?.firstName,
      lastName: existingUser?.lastName,
      email: existingUser?.email,
      languageId: getLanguageId(),
      address: {
        addressLine1: existingAddress?.addressLine1,
        addressLine2: existingAddress?.addressLine2,
        city: existingAddress?.city,
        stateOrProvince: existingAddress?.stateOrProvince,
        country: existingAddress?.country,
        zipOrPostalCode: existingAddress?.zipOrPostalCode,
      },
    },
  };
};
